import { Button, Col, InputNumber, Row } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ROLE } from "common/constant";
import useAuth from "common/hooks/useAuth";

import { masterDataApi } from "services/masterData";
import { serviceApi } from "services/service";
import { userApi } from "services/user";

import { pulldownStore } from "store/slices/pulldown";
import { getListOptOfficesAction } from "store/slices/pulldown";

import OperationMultiSelect from "./OperationMultiSelect";

const OperationHeader = ({
  numService, onSearch, autoRefreshMinutes, onAutoRefreshChange,
}) => {
  const { listOptOffices } = useSelector(pulldownStore);
  const [listDriver, setListDriver] = useState([]);
  const [listCarNumber, setListCarNumber] = useState([]);

  // Multi-select values (controlled)
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectedCarNumbers, setSelectedCarNumbers] = useState([]);

  // Track if saved filter has been loaded
  const [savedFilterLoaded, setSavedFilterLoaded] = useState(false);
  // Track saved driver/car values to restore after list loads
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [pendingCarNumbers, setPendingCarNumbers] = useState([]);

  const { role, branchOfficeCd } = useAuth();
  const dispatch = useDispatch();

  // Load drivers and car numbers when selected offices change
  useEffect(() => {
    const loadData = async () => {
      if (selectedOffices.length > 0) {
        // Load drivers and car numbers for all selected offices
        const allDrivers = [];
        const allCarNumbers = [];

        for (const officeCd of selectedOffices) {
          const drivers = await masterDataApi.getDriver({ office_cd: officeCd });
          const carNums = await serviceApi.getListCarNum({ office_cd: officeCd });

          drivers.forEach((driver) => {
            const firstName = driver.driver_nm_first
              ? ` ${driver.driver_nm_first}`
              : "";
            const driverOption = {
              value: driver.driver_cd,
              label: driver.driver_nm_last + firstName
            };
            if (!allDrivers.find((d) => d.value === driverOption.value)) {
              allDrivers.push(driverOption);
            }
          });

          carNums.forEach((car) => {
            const carOption = {
              value: car.car_number,
              label: car.car_number
            };
            if (!allCarNumbers.find((c) => c.value === carOption.value)) {
              allCarNumbers.push(carOption);
            }
          });
        }

        setListDriver(allDrivers);
        setListCarNumber(allCarNumbers);

        // Restore pending values from saved filter (if any)
        if (pendingDrivers.length > 0) {
          setSelectedDrivers(pendingDrivers);
          setPendingDrivers([]);
        } else {
          setSelectedDrivers([]);
        }

        if (pendingCarNumbers.length > 0) {
          setSelectedCarNumbers(pendingCarNumbers);
          setPendingCarNumbers([]);
        } else {
          setSelectedCarNumbers([]);
        }
      } else {
        setListDriver([]);
        setListCarNumber([]);
        setSelectedDrivers([]);
        setSelectedCarNumbers([]);
      }
    };

    loadData();
  }, [selectedOffices]);

  useEffect(() => {
    dispatch(getListOptOfficesAction("operation"));
  }, []);

  // Load saved filter from backend and trigger initial search
  useEffect(() => {
    const loadSavedFilter = async () => {
      try {
        const response = await userApi.getOperationFilter();
        if (response) {
          const { office_cds, driver_cds, car_numbers } = response;

          // If saved filter has offices, use it
          if (office_cds?.length > 0) {
            // Save pending values to restore after lists are loaded
            if (driver_cds?.length > 0) {
              setPendingDrivers(driver_cds);
            }
            if (car_numbers?.length > 0) {
              setPendingCarNumbers(car_numbers);
            }

            setSelectedOffices(office_cds);
            setSavedFilterLoaded(true);

            // Trigger search with saved filter
            const searchParams = { office_cds };
            if (driver_cds?.length > 0) {
              searchParams.driver_cds = driver_cds;
            }
            if (car_numbers?.length > 0) {
              searchParams.car_numbers = car_numbers;
            }
            onSearch(searchParams);
            return;
          }
        }

        // No saved filter - use role-based defaults
        if (role === ROLE.SUB_ADMIN && branchOfficeCd && branchOfficeCd !== "0000") {
          setSelectedOffices([branchOfficeCd]);
          onSearch({ office_cds: [branchOfficeCd] });
        } else {
          // ADMIN or no specific office - search all
          onSearch({});
        }
      } catch (error) {
        console.error("Failed to load saved filter:", error);
        // On error, still trigger search with defaults
        if (role === ROLE.SUB_ADMIN && branchOfficeCd && branchOfficeCd !== "0000") {
          onSearch({ office_cds: [branchOfficeCd] });
        } else {
          onSearch({});
        }
      }
    };
    loadSavedFilter();
  }, [role, branchOfficeCd]);

  // Set initial values based on role (only if no saved filter)
  useEffect(() => {
    if (savedFilterLoaded) {
      return;
    }

    if (role === ROLE.SUB_ADMIN && listOptOffices?.length > 0 && branchOfficeCd) {
      setSelectedOffices([branchOfficeCd]);
    }
  }, [listOptOffices, role, branchOfficeCd, savedFilterLoaded]);

  const handleSubmitForm = async () => {
    // Build search params
    const searchParams = {};
    if (selectedOffices.length > 0) {
      searchParams.office_cds = selectedOffices;
    }
    if (selectedDrivers.length > 0) {
      searchParams.driver_cds = selectedDrivers;
    }
    if (selectedCarNumbers.length > 0) {
      searchParams.car_numbers = selectedCarNumbers;
    }

    // Save filter to backend
    try {
      await userApi.saveOperationFilter({
        office_cds: selectedOffices,
        driver_cds: selectedDrivers,
        car_numbers: selectedCarNumbers
      });
    } catch (error) {
      console.error("Failed to save filter:", error);
    }

    onSearch(searchParams);
  };

  return (
    <div className="operation-header">
      <Row align="middle" className="bg-white">
        {/* Phần 1: Số lượng xe - width 270px giống left sidebar */}
        <Col flex="270px" className="header-left">
          <div className="dis-flex ai-center pl-10">
            <div className="fs-32 fw-700 mr-6 mb-6">{numService}</div>
            <div>台 稼働中</div>
          </div>
        </Col>

        {/* Phần 2: Search filters - ở giữa */}
        <Col flex="auto" className="header-center">
          <div className="filter-fields-row">
            <OperationMultiSelect
              label="営業所"
              listOptions={listOptOffices || []}
              value={selectedOffices}
              onChange={setSelectedOffices}
              width={180}
              dropdownWidth={280}
            />
            <OperationMultiSelect
              label="ドライバー"
              listOptions={listDriver}
              value={selectedDrivers}
              onChange={setSelectedDrivers}
              width={180}
              dropdownWidth={280}
              disabled={selectedOffices.length === 0}
            />
            <OperationMultiSelect
              label="車両番号"
              listOptions={listCarNumber}
              value={selectedCarNumbers}
              onChange={setSelectedCarNumbers}
              width={180}
              dropdownWidth={280}
              disabled={selectedOffices.length === 0}
            />
            <Button type="primary" className="filter-submit-btn" onClick={handleSubmitForm}>
              絞り込み
            </Button>
          </div>
        </Col>

        {/* Phần 3: Auto refresh - bên phải */}
        <Col className="header-right pr-16">
          <div className="auto-refresh-wrapper">
            <span className="auto-refresh-label">自動更新</span>
            <InputNumber
              min={0}
              max={60}
              value={autoRefreshMinutes}
              onChange={onAutoRefreshChange}
              className="auto-refresh-input"
            />
            <span className="auto-refresh-unit">分</span>
          </div>
          <div className="auto-refresh-hint">(最短1分、更新しない場合0分)</div>
        </Col>

      </Row>
    </div>
  );
};

OperationHeader.propTypes = {
  numService: PropTypes.number,
  onSearch: PropTypes.func,
  autoRefreshMinutes: PropTypes.number,
  onAutoRefreshChange: PropTypes.func,
};

export default OperationHeader;
